import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaPlane, FaMoneyBillWave, FaCalendar, FaInfoCircle, FaCheckCircle, FaQuestionCircle } from 'react-icons/fa';
import Chart from 'react-apexcharts';

const VacationSavingsCalculator = () => {
  const [formData, setFormData] = useState({
    vacationCost: '',
    monthlySavings: '',
    currentSavings: '0',
    monthsToSave: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    // Initialize chart data
    const chartOptions = {
      series: [0, 0],
      options: {
        chart: {
          type: 'donut',
          height: 350,
          toolbar: {
            show: false
          }
        },
        colors: ['#4361ee', '#8338ec'],
        labels: ['Current Savings', 'Additional Savings Needed'],
        legend: {
          position: 'bottom',
          fontSize: '14px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          labels: {
            colors: '#666',
            useSeriesColors: true
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      }
    };
    setChartData(chartOptions);
  }, []);

  const calculateSavings = () => {
    const { vacationCost, monthlySavings, currentSavings, monthsToSave } = formData;

    if (!vacationCost || !monthlySavings || !monthsToSave) {
      setError('Please fill in all required fields');
      return;
    }

    const cost = parseFloat(vacationCost);
    const savings = parseFloat(monthlySavings);
    const current = parseFloat(currentSavings);
    const months = parseFloat(monthsToSave);

    if (isNaN(cost) || isNaN(savings) || isNaN(months) || cost <= 0 || savings <= 0 || months <= 0) {
      setError('Please enter valid numbers for all fields');
      return;
    }

    // Calculate total savings needed
    const totalNeeded = cost - current;
    const totalSavings = savings * months;
    const totalGap = totalNeeded - totalSavings;

    const monthsToReachGoal = Math.ceil(totalNeeded / savings);
    const totalMonths = monthsToReachGoal > months ? monthsToReachGoal : months;

    setResults({
      totalNeeded: `₹${totalNeeded.toFixed(2)}`,
      totalSavings: `₹${totalSavings.toFixed(2)}`,
      totalGap: `₹${totalGap.toFixed(2)}`,
      monthsToReachGoal: monthsToReachGoal,
      totalMonths: totalMonths,
      currentSavings: `₹${current.toFixed(2)}`
    });

    // Update chart data
    if (chartData) {
      const updatedData = {
        ...chartData,
        series: [current, totalNeeded]
      };
      setChartData(updatedData);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Vacation Savings Calculator | Financial Tool</title>
        <meta name="description" content="Plan and save for your dream vacation with our vacation savings calculator." />
        <meta name="keywords" content="vacation savings calculator, travel savings, vacation planning, savings calculator" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="calculator-header mb-12">
          <div className="flex items-center gap-4">
            <div className="header-icon text-4xl">
              <FaPlane />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Vacation Savings Calculator</h1>
              <p className="text-lg text-blue-600 font-semibold">Plan Your Dream Vacation</p>
            </div>
          </div>
        </div>

        <div className="calculator-description space-y-4 mb-12">
          <p className="text-gray-700">
            Use our vacation savings calculator to plan and save for your dream vacation. Enter your vacation cost,
            current savings, and monthly savings amount to see how long it will take to reach your goal.
          </p>
        </div>

        <div className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="vacationCost">
                Total Vacation Cost
              </label>
              <input
                type="number"
                id="vacationCost"
                name="vacationCost"
                value={formData.vacationCost}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="₹0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="currentSavings">
                Current Savings
              </label>
              <input
                type="number"
                id="currentSavings"
                name="currentSavings"
                value={formData.currentSavings}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="₹0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="monthlySavings">
                Monthly Savings Amount
              </label>
              <input
                type="number"
                id="monthlySavings"
                name="monthlySavings"
                value={formData.monthlySavings}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="₹0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="monthsToSave">
                Number of Months to Save
              </label>
              <input
                type="number"
                id="monthsToSave"
                name="monthsToSave"
                value={formData.monthsToSave}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="0"
              />
            </div>

            <button
              onClick={calculateSavings}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-md text-sm font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Calculate Savings
            </button>

            {error && (
              <div className="text-red-500 text-center mt-4">
                {error}
              </div>
            )}
          </div>

          {results && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Total Amount Needed</h3>
                  <p className="text-xl font-medium">{results.totalNeeded}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Current Savings</h3>
                  <p className="text-xl font-medium">{results.currentSavings}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Total Savings</h3>
                  <p className="text-xl font-medium">{results.totalSavings}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Additional Savings Needed</h3>
                  <p className="text-xl font-medium">{results.totalGap}</p>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Savings Timeline</h3>
                <p className="text-gray-700">
                  You need to save for {results.monthsToReachGoal} months to reach your vacation goal.
                  If you continue saving for {results.totalMonths} months, you will reach your goal sooner.
                </p>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Distribution Visualization</h3>
                <div className="h-96">
                  {chartData && (
                    <Chart
                      options={chartData.options}
                      series={chartData.series}
                      type="donut"
                      height={350}
                    />
                  )}
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Explanation</h3>
                <p className="text-gray-700">
                  Based on your inputs:
                  <br />
                  • Total amount needed for vacation: {results.totalNeeded}
                  <br />
                  • Current savings: {results.currentSavings}
                  <br />
                  • Monthly savings amount: ₹{formData.monthlySavings}
                  <br />
                  • Total months to save: {results.totalMonths}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            How to Use Vacation Savings Calculator <FaInfoCircle className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <ol className="list-decimal pl-8 space-y-4">
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Enter your total vacation cost</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Input your current savings amount</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Enter your monthly savings amount</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Enter the number of months you plan to save</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Click "Calculate Savings" to see your results</span>
              </li>
            </ol>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Features and Benefits <FaMoneyBillWave className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Vacation cost estimation</li>
                  <li>Current savings tracking</li>
                  <li>Monthly savings planning</li>
                  <li>Savings timeline calculation</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Benefits</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Plan your vacation budget effectively</li>
                  <li>Track your savings progress</li>
                  <li>Stay motivated with clear goals</li>
                  <li>Make informed financial decisions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Frequently Asked Questions <FaQuestionCircle className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is this calculator for?</h3>
                <p className="text-gray-700">
                  This calculator helps you plan and save for your vacation by calculating how much you need to save monthly to reach your goal.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How accurate is this calculator?</h3>
                <p className="text-gray-700">
                  This calculator provides an estimate based on the inputs you provide. Actual results may vary based on additional expenses and other factors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VacationSavingsCalculator;
